import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { TOOL_CATEGORIES } from '@/shared/constants/category-colors';
import { FilterChipPopover } from '@/shared/ui/FilterChipPopover';
import { SearchInput } from '@/shared/ui';

import type { ToolsGridToolbarProps } from './ToolsGridToolbar.types';

/**
 * The filter row above the tools grid: a name search, and the two things a tool
 * is — its category and whether it is an app or an agent.
 *
 * The grid owns the filter state, because it is the grid that narrows against
 * it; the toolbar only reports what the user picked.
 */
export function ToolsGridToolbar({ filters, onChange, categoryLabels }: ToolsGridToolbarProps) {
  const { t } = useTranslation('tools');
  const searchLabel = t('toolsHub.toolCatalog.searchToolsByNameLabel');

  const categoryOptions = useMemo(
    () =>
      TOOL_CATEGORIES.map((id) => ({
        value: id,
        label: categoryLabels?.[id] ?? t(`toolsHub.toolsHubConstants.${id}`),
      })),
    [categoryLabels, t],
  );

  const kindOptions = useMemo(
    () => [
      { value: 'app' as const, label: t('toolsHub.toolCatalog.apps') },
      { value: 'agent' as const, label: t('toolsHub.toolCatalog.agents') },
    ],
    [t],
  );

  return (
    <div data-testid="tools-grid-toolbar" className="flex flex-wrap items-center gap-xs">
      <SearchInput
        value={filters.search}
        onChange={(search) => onChange({ ...filters, search })}
        className="w-80"
        placeholder={t('toolsHub.toolCatalog.searchToolsByName')}
        aria-label={searchLabel}
      />
      <FilterChipPopover
        label={t('toolsHub.toolCatalog.category')}
        options={categoryOptions}
        value={filters.category}
        onChange={(category) => onChange({ ...filters, category })}
        searchable={false}
        resetOptionLabel={t('toolsHub.toolCatalog.allCategories')}
      />
      <FilterChipPopover
        label={t('toolsHub.toolCatalog.type')}
        options={kindOptions}
        value={filters.kind}
        onChange={(kind) => onChange({ ...filters, kind })}
        searchable={false}
        resetOptionLabel={t('toolsHub.toolCatalog.allTypes')}
      />
    </div>
  );
}

ToolsGridToolbar.displayName = 'ToolsGridToolbar';
